import { IQuoteTemplateConfigSchema } from '@monetize/types/app';
import {
  ExperienceConfigurationKeys,
  QuoteTemplateSettingsConfig,
} from '@monetize/types/experience-config';
import { Control, UseFormSetValue } from 'react-hook-form';
import { MdInfo } from 'react-icons/md';
import { useExperienceConfiguration } from '../../../hooks';
import { useFlags } from '../../../services/launchDarkly';
import { MFlex, MIcon, MStack, MText, MTooltip } from '../../Monetize';
import { QuoteTemplateSettingCard } from './QuoteTemplateSettingCard';
import {
  QUOTE_TEMPLATE_SETTINGS_METADATA,
  QuoteTemplateSettingMeta,
} from './quoteTemplateSettings.metadata';
import { isSettingFlagEnabled } from './quoteTemplateSettings.utils';

const SECTIONS: { key: QuoteTemplateSettingMeta['section']; label: string }[] =
  [
    { key: 'includedSections', label: 'Included Sections in Quote' },
    { key: 'offeringDetails', label: 'Included Details for Offerings' },
  ];

const DEFAULT_SETTING_TOOLTIP =
  'The toggle value will be applied to newly created quotes. Expand a setting to choose which roles can view or edit it.';

interface QuoteTemplateSettingsListProps {
  valuesControl: Control<IQuoteTemplateConfigSchema>;
  permissionsControl: Control<QuoteTemplateSettingsConfig>;
  permissionsSetValue: UseFormSetValue<QuoteTemplateSettingsConfig>;
  isDisabled?: boolean;
  isAmendmentV2Quote?: boolean;
}

/**
 * The Quote Templates settings-page list: every customizable setting rendered
 * as a card with its default toggle and role-based visibility controls,
 * grouped into the same sections used in the per-quote customize view.
 */
export const QuoteTemplateSettingsList = ({
  valuesControl,
  permissionsControl,
  permissionsSetValue,
  isDisabled,
  isAmendmentV2Quote,
}: QuoteTemplateSettingsListProps) => {
  const { hideBillingSchedule } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );
  const flags = useFlags();

  const isVisible = (meta: QuoteTemplateSettingMeta) => {
    if (meta.isAmendmentOnly && !isAmendmentV2Quote) {
      return false;
    }
    if (meta.requiresBillingScheduleEnabled && hideBillingSchedule) {
      return false;
    }
    // A flagged-off setting must not be configurable here either, or an admin
    // could set a tenant default that ships the feature to every new quote.
    return isSettingFlagEnabled(meta, flags);
  };

  return (
    <MStack gap={6} mt={2}>
      {SECTIONS.map(({ key: sectionKey, label }) => {
        const settings = QUOTE_TEMPLATE_SETTINGS_METADATA.filter(
          (meta) => meta.section === sectionKey && isVisible(meta),
        );

        if (settings.length === 0) {
          return null;
        }

        return (
          <MStack key={sectionKey} gap={2}>
            <MFlex alignItems="baseline" justifyContent="space-between">
              <MText fontWeight="bold">{label}</MText>
              {/* Column caption for the card toggles. Right padding aligns its
                  end with the toggles: 16px card padding + 32px expand button
                  + 12px gap. */}
              <MText fontWeight="bold" pr="60px">
                Default Setting
                <MTooltip
                  label={DEFAULT_SETTING_TOOLTIP}
                  placement="top"
                  shouldWrapChildren
                >
                  <MIcon
                    as={MdInfo}
                    color="tPurple.base"
                    boxSize={4}
                    ml={1}
                    verticalAlign="text-bottom"
                  />
                </MTooltip>
              </MText>
            </MFlex>
            {settings.map((meta) => (
              <QuoteTemplateSettingCard
                key={meta.key}
                meta={meta}
                valuesControl={valuesControl}
                permissionsControl={permissionsControl}
                permissionsSetValue={permissionsSetValue}
                isDisabled={isDisabled}
              />
            ))}
          </MStack>
        );
      })}
    </MStack>
  );
};
